/**
 * AssistantPage — the conversational helpdesk surface (AAS). Opens a session, lets STAFF ask
 * operational questions, and renders each assistant turn with: the answer text, the cited chunk
 * *titres* (the RBAC-scoped retrieval), the bilingual non-clinical disclaimer, and the governance
 * status (abstenu = a clinical request refused, disabled = no AI key on this install, error =
 * non-blocking failure). The assistant is NEVER in an operational control path: every failure degrades
 * to a polite notice, never an exception.
 *
 * 5-state DoD (vide / chargement / erreur / hors-ligne / sans-droit):
 *   - sans-droit : ForbiddenState when ai.assistant.query is missing (also enforced by ProtectedRoute).
 *   - hors-ligne : OfflineState (the LLM is agent-proxied; offline = no answer).
 *   - chargement : the session-open spinner; the per-message "Réflexion…" indicator.
 *   - erreur     : a session-open failure (the ask path itself never throws — it returns a tagged status).
 *                  A 404 / module-un-mounted renders the clean ModuleUnavailable state.
 *   - vide       : the empty conversation prompt before the first question.
 *
 * Keyboard-first: the composer submits on Enter (Shift+Enter = newline); the input auto-focuses after
 * each answer. FR copy + AR-RTL aware (CSS logical properties; mono/LTR islands for the version chip).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  OfflineState,
  Spinner,
  Textarea,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { AI_CAPS } from "../caps";
import { useAsk, useOpenSession } from "../api";
import { isNotMounted, statusVisual } from "../lib";
import type { Answer, Turn } from "../types";
import { PageHeader } from "../components/PageHeader";
import { Disclaimer } from "../components/Disclaimer";
import { ChunkCitations } from "../components/ChunkCitations";
import { StatusNotice } from "../components/StatusNotice";
import { ModuleUnavailable } from "../components/ModuleUnavailable";

export function AssistantPage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const canQuery = useCan(AI_CAPS.query);

  const open = useOpenSession();
  const ask = useAsk();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const seq = useRef(0);

  const startSession = useCallback(() => {
    open.reset();
    open.mutate(undefined, {
      onSuccess: (res) => {
        setSessionId(String(res.session_id));
        setTurns([]);
        requestAnimationFrame(() => inputRef.current?.focus());
      },
    });
  }, [open]);

  // Auto-open a session on first mount (once the user may query + is online).
  useEffect(() => {
    if (canQuery && online && sessionId === null && !open.isPending && !open.isError) {
      startSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canQuery, online, sessionId]);

  const submit = useCallback(() => {
    const requete = draft.trim();
    if (!requete || !sessionId || ask.isPending) return;
    const userKey = `u-${seq.current++}`;
    setTurns((prev) => [...prev, { key: userKey, role: "utilisateur", texte: requete }]);
    setDraft("");
    ask.mutate(
      { sessionId, requete },
      {
        onSuccess: (answer: Answer) => {
          setTurns((prev) => [
            ...prev,
            {
              key: `a-${seq.current++}`,
              role: "assistant",
              texte: answer.texte,
              status: answer.status,
              chunks: answer.chunks_utilises,
              disclaimer: answer.disclaimer,
            },
          ]);
          requestAnimationFrame(() => inputRef.current?.focus());
        },
      },
    );
  }, [draft, sessionId, ask]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const header = (
    <PageHeader
      title={t("ai.assistant.title")}
      subtitle={t("ai.assistant.subtitle")}
      badge={
        sessionId ? (
          <Badge tone="info" glyph="◷">
            <span className="nv-ltr-island">
              {t("ai.assistant.session")} #{sessionId}
            </span>
          </Badge>
        ) : undefined
      }
      actions={
        sessionId ? (
          <Button variant="secondary" onClick={startSession} disabled={open.isPending}>
            {t("ai.assistant.restart")}
          </Button>
        ) : undefined
      }
    />
  );

  // ── 5-state DoD, in priority order ──────────────────────────────────────────────
  if (!canQuery) return <ForbiddenState requiredCapabilities={[AI_CAPS.query]} />;

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={startSession} cached={false} />
      </Screen>
    );
  }

  // A 404 on session-open = the module is not mounted on this install → clean unavailable state.
  if (open.isError && isNotMounted(open.error)) {
    return (
      <Screen>
        {header}
        <ModuleUnavailable />
      </Screen>
    );
  }

  if (open.isError) {
    return (
      <Screen>
        {header}
        <ErrorState
          title={t("ai.assistant.openError")}
          message={open.error.message}
          code={open.error.code}
          onRetry={startSession}
        />
      </Screen>
    );
  }

  if (!sessionId || open.isPending) {
    return (
      <Screen>
        {header}
        <Card>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--nv-s-3)",
              color: "var(--nv-slate)",
            }}
          >
            <Spinner />
            <span>{t("ai.assistant.opening")}</span>
          </div>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      <Card padded={false}>
        <div
          role="log"
          aria-label={t("ai.assistant.title")}
          aria-live="polite"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--nv-s-4)",
            padding: "var(--nv-s-4)",
            minHeight: "280px",
            maxHeight: "52vh",
            overflowY: "auto",
          }}
        >
          {turns.length === 0 ? (
            <div
              style={{
                margin: "auto",
                textAlign: "center",
                color: "var(--nv-slate)",
                maxWidth: "46ch",
              }}
            >
              <div style={{ fontSize: "var(--nv-fs-h3)", marginBottom: "var(--nv-s-2)" }}>
                {t("ai.assistant.empty.title")}
              </div>
              <p style={{ margin: 0 }}>{t("ai.assistant.empty.desc")}</p>
            </div>
          ) : (
            turns.map((turn) => <TurnView key={turn.key} turn={turn} />)
          )}

          {ask.isPending && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--nv-s-2)",
                color: "var(--nv-steel)",
                fontSize: "var(--nv-fs-caption)",
              }}
            >
              <Spinner />
              <span>{t("ai.assistant.sending")}</span>
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--nv-s-2)",
            padding: "var(--nv-s-3)",
            borderTop: "1px solid var(--nv-line-faint)",
          }}
        >
          <Textarea
            ref={inputRef}
            value={draft}
            rows={2}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("ai.assistant.placeholder")}
            aria-label={t("ai.assistant.placeholder")}
            disabled={ask.isPending}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--nv-s-2)",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-steel)" }}>
              {t("ai.assistant.hint")}
            </span>
            <Button type="submit" disabled={!draft.trim() || ask.isPending}>
              {ask.isPending ? t("ai.assistant.sending") : t("ai.assistant.send")}
            </Button>
          </div>
        </form>
      </Card>
    </Screen>
  );
}

/** One conversation turn — a user question or an assistant answer (with status + citations + disclaimer). */
function TurnView({ turn }: { turn: Turn }) {
  const { t } = useT();
  const isUser = turn.role === "utilisateur";
  const v = turn.status ? statusVisual(turn.status) : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-2)",
        alignItems: isUser ? "flex-end" : "flex-start",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--nv-s-2)",
          fontSize: "var(--nv-fs-label)",
          color: "var(--nv-slate)",
        }}
      >
        <span>{isUser ? t("ai.assistant.you") : t("ai.assistant.assistant")}</span>
        {v && (
          <Badge tone={v.tone} glyph={v.glyph}>
            {t(v.labelKey)}
          </Badge>
        )}
      </div>

      <div
        style={{
          maxWidth: "72ch",
          padding: "var(--nv-s-3)",
          borderRadius: "var(--nv-r-md)",
          background: isUser ? "var(--nv-azure-soft)" : "var(--nv-surface-2)",
          border: "1px solid var(--nv-line-faint)",
          whiteSpace: "pre-wrap",
          lineHeight: 1.55,
        }}
      >
        {turn.texte}
      </div>

      {!isUser && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--nv-s-2)",
            maxWidth: "72ch",
            width: "100%",
          }}
        >
          {turn.status && turn.status !== "ok" && <StatusNotice status={turn.status} />}
          {turn.status === "ok" && turn.chunks && <ChunkCitations titres={turn.chunks} />}
          {turn.disclaimer && <Disclaimer text={turn.disclaimer} />}
        </div>
      )}
    </div>
  );
}

/** The shared vertical-stack frame each screen state shares. */
function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>
  );
}
